import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotificationCenter } from "../context/NotificationContext";

const ALERT_EVENT = "buzzer-klip-campaign-extension-alerts";
export const CAMPAIGN_EXTENSION_REFRESH_EVENT = "buzzer-klip-campaign-extension-refresh";

function storageKey(user) {
  return user?.id ? `bk_campaign_extension_seen_${user.id}` : "";
}

function noticeKey(item) {
  return item?.notification_key || `${item?.campaign_id || item?.id || ""}:${item?.extended_at || ""}`;
}

function readSeen(key) {
  if (!key || typeof window === "undefined") return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeSeen(key, ids) {
  if (!key || typeof window === "undefined") return;
  const unique = Array.from(new Set(ids.filter(Boolean))).slice(-300);
  window.localStorage.setItem(key, JSON.stringify(unique));
  window.dispatchEvent(new CustomEvent(ALERT_EVENT, { detail: { key, ids: unique } }));
}

export default function useCampaignExtensionAlerts(user, { limit = 5 } = {}) {
  const enabled = Boolean(user?.id);
  const key = storageKey(user);
  const { extensions, refresh: refreshNotifications } = useNotificationCenter();
  const notifications = useMemo(
    () => enabled && Array.isArray(extensions?.notifications) ? extensions.notifications : [],
    [enabled, extensions],
  );
  const [seenIds, setSeenIds] = useState(() => readSeen(key));

  useEffect(() => {
    setSeenIds(readSeen(key));
  }, [key]);

  useEffect(() => {
    const syncSeen = (event) => {
      if (event?.detail?.key && event.detail.key !== key) return;
      setSeenIds(readSeen(key));
    };
    window.addEventListener("storage", syncSeen);
    window.addEventListener(ALERT_EVENT, syncSeen);
    return () => {
      window.removeEventListener("storage", syncSeen);
      window.removeEventListener(ALERT_EVENT, syncSeen);
    };
  }, [key]);

  const unseenNotifications = useMemo(() => {
    const seen = new Set(seenIds);
    return notifications.filter((item) => !seen.has(noticeKey(item))).slice(0, limit);
  }, [notifications, seenIds, limit]);

  const markSeen = useCallback((items = notifications) => {
    const nextIds = [
      ...seenIds,
      ...items.map(noticeKey),
    ];
    writeSeen(key, nextIds);
    setSeenIds(readSeen(key));
  }, [key, notifications, seenIds]);

  return {
    notifications,
    unseenNotifications,
    unseenCount: unseenNotifications.length,
    markSeen,
    refresh: () => refreshNotifications({ force: true }),
    noticeKey,
  };
}
